import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import ThemeToggle from '../components/ThemeToggle';
import { useBrand } from '../branding/BrandContext';
import { useTheme } from '../context/ThemeContext';
import './Login.css';

const STRINGS = {
  en: { tagline: 'Sign in to your account', username: 'Username', password: 'Password', userPh: 'Enter your username', passPh: 'Enter your password', signIn: 'Sign In', signingIn: 'Signing in…', failed: 'Login failed', alt: 'Mize Pro', switchTo: 'العربية' },
  ar: { tagline: 'سجّل الدخول إلى حسابك', username: 'اسم المستخدم', password: 'كلمة المرور', userPh: 'أدخل اسم المستخدم', passPh: 'أدخل كلمة المرور', signIn: 'تسجيل الدخول', signingIn: 'جارٍ الدخول…', failed: 'فشل تسجيل الدخول', alt: 'مايز برو', switchTo: 'EN' },
} as const;

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const lang = language === 'ar' ? 'ar' : 'en';
  const t = STRINGS[lang];
  // A white-label workshop's device (or its ?brand= link) gets that workshop's sign-in page.
  const brand = useBrand();
  const { theme } = useTheme();

  // Phones get no starfield at all: 106 twinkling nodes, three blurred nebulas
  // and a mousemove parallax listener are pure decoration, and this is the first
  // screen every user meets on a cold cache. Desktop is unchanged.
  const plain =
    typeof window !== 'undefined' && !!window.matchMedia &&
    (window.matchMedia('(max-width: 768px)').matches ||
     window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
     !!(navigator as any)?.connection?.saveData);

  // Starfield — generated once, grouped into depth layers for cursor parallax.
  const starLayers = useMemo(() => {
    if (plain) return [];
    const rnd = (a: number, b: number) => a + Math.random() * (b - a);
    const defs = [
      { key: 'far', count: 54, sMin: 0.8, sMax: 1.8 },
      { key: 'mid', count: 34, sMin: 1.3, sMax: 2.6 },
      { key: 'near', count: 18, sMin: 2, sMax: 3.8 },
    ];
    return defs.map((d) => ({
      key: d.key,
      stars: Array.from({ length: d.count }, (_, i) => ({
        id: i,
        top: rnd(0, 100),
        left: rnd(0, 100),
        size: rnd(d.sMin, d.sMax),
        o: rnd(0.45, 1),
        dur: rnd(2.4, 6),
        delay: rnd(0, 6),
        gold: Math.random() < 0.3,
      })),
    }));
  }, [plain]);

  // Shapes follow the cursor (parallax): publish the normalized cursor offset
  // (−0.5 … 0.5) as CSS vars; each shape translates by a different depth factor.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || plain) return;   // no cursor on a phone — don't even listen
    const onMove = (e: MouseEvent) => {
      el.style.setProperty('--mx', ((e.clientX / window.innerWidth) - 0.5).toFixed(4));
      el.style.setProperty('--my', ((e.clientY / window.innerHeight) - 0.5).toFixed(4));
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [plain]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/');
    } catch (err: any) {
      setError(err.message || t.failed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container" ref={containerRef}>
      {/* Starry space — layered twinkling stars that drift with the cursor */}
      <div className="login-shapes" aria-hidden="true">
        {!plain && <span className="nebula nebula-1" />}
        {!plain && <span className="nebula nebula-2" />}
        {!plain && <span className="nebula nebula-3" />}
        {starLayers.map((layer) => (
          <div key={layer.key} className={`star-layer star-${layer.key}`}>
            {layer.stars.map((s) => (
              <span
                key={s.id}
                className={`star${s.gold ? ' gold' : ''}`}
                style={{
                  top: `${s.top}%`,
                  left: `${s.left}%`,
                  width: `${s.size}px`,
                  height: `${s.size}px`,
                  animationDuration: `${s.dur}s`,
                  animationDelay: `${s.delay}s`,
                  ['--o' as any]: s.o,
                } as React.CSSProperties}
              />
            ))}
          </div>
        ))}
        {!plain && <span className="shooting-star ss-1" />}
        {!plain && <span className="shooting-star ss-2" />}
      </div>

      <div className="login-topbar">
        <button type="button" className="lang-toggle" onClick={() => changeLanguage(lang === 'ar' ? 'en' : 'ar')} aria-label="Switch language">
          {t.switchTo}
        </button>
        <ThemeToggle />
      </div>

      <div className="login-card animate-fade-in">
        <div className="login-header">
          <div className="login-logo-wrap">
            {brand ? (
              <img src={theme === 'dark' ? brand.logoOnDark : brand.logo} alt={brand.name[lang]} className="login-logo login-logo-brand" />
            ) : (
              <img src="/mize-pro-logo.png" alt={t.alt} className="login-logo" />
            )}
          </div>
          <p>{t.tagline}</p>
        </div>

        {error && <div className="login-error animate-slide-down">{error}</div>}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label className="label">{t.username}</label>
            <input
              type="text"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder={t.userPh}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label className="label">{t.password}</label>
            <input
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={t.passPh}
              autoComplete="current-password"
            />
          </div>

          <button type="submit" className="btn btn-primary login-button" disabled={loading}>
            {loading ? (
              <span className="button-loading">
                <span className="spinner-small" />
                {t.signingIn}
              </span>
            ) : (
              t.signIn
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
